import React, { useState, useEffect } from 'react';
import { getWhatsAppConfig, updateWhatsAppConfig, generateWhatsAppUrl, validatePhoneNumber, formatPhoneNumberForDisplay, formatPhoneNumberForUrl, getCountryCodes } from '../../services/whatsappService';
import { useAdminAuth } from '../../hooks/useAdminAuth';

function WhatsAppManagement() {
  const { isAdmin } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [config, setConfig] = useState({
    services: [
      { id: '1', label: 'Customer Service 1', phoneNumber: '+14083335900', message: '' },
      { id: '2', label: 'Customer Service 2', phoneNumber: '', message: '' },
      { id: '3', label: 'Customer Service 3', phoneNumber: '', message: '' }
    ],
    defaultMessage: 'Hello, I need assistance with your service',
    serviceName: 'Circana'
  });
  
  const [selectedCountryCodes, setSelectedCountryCodes] = useState({
    '1': '+1',
    '2': '+92',
    '3': '+92'
  });
  
  const countryCodes = getCountryCodes();
  
  const [originalConfig, setOriginalConfig] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});

  useEffect(() => {
    if (isAdmin) {
      loadConfig();
    }
  }, [isAdmin]);

  const loadConfig = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getWhatsAppConfig();
      // Format phone numbers for display (ensure + sign is present)
      const formattedData = {
        ...data,
        services: data.services.map(service => ({
          ...service,
          phoneNumber: service.phoneNumber ? formatPhoneNumberForDisplay(service.phoneNumber) : ''
        }))
      };
      
      setConfig(formattedData);
      setOriginalConfig(JSON.parse(JSON.stringify(formattedData)));
      
      // Set country codes based on phone numbers
      const codes = {};
      formattedData.services.forEach(service => {
        if (service.phoneNumber) {
          const match = service.phoneNumber.match(/^\+(\d{1,4})/);
          if (match) {
            codes[service.id] = '+' + match[1];
          } else {
            codes[service.id] = '+1'; // Default to USA
          }
        } else {
          codes[service.id] = service.id === '1' ? '+1' : '+92'; // Default: USA for CS1, Pakistan for others
        }
      });
      setSelectedCountryCodes(codes);
      
      updatePreviewUrls(formattedData);
    } catch (err) {
      console.error('Error loading config:', err);
      setError('Failed to load WhatsApp configuration');
    } finally {
      setLoading(false);
    }
  };

  const updatePreviewUrls = (configData) => {
    const urls = {};
    configData.services.forEach(service => {
      if (service.phoneNumber) {
        try {
          urls[service.id] = generateWhatsAppUrl(service.phoneNumber, service.message || configData.defaultMessage);
        } catch (err) {
          urls[service.id] = 'Invalid phone number';
        }
      } else {
        urls[service.id] = 'Enter phone number to see preview';
      }
    });
    setPreviewUrls(urls);
  };

  const handleServiceChange = (serviceId, field, value) => {
    setConfig(prev => {
      const updated = {
        ...prev,
        services: prev.services.map(service => {
          if (service.id === serviceId) {
            const updatedService = { ...service, [field]: value };
            
            // If message is empty, use default message
            if (field === 'phoneNumber' && !updatedService.message) {
              updatedService.message = prev.defaultMessage;
            }
            
            // Format phone number for display (preserve + sign)
            if (field === 'phoneNumber') {
              updatedService.phoneNumber = formatPhoneNumberForDisplay(value);
              
              // Update country code dropdown if phone number starts with a country code
              const match = updatedService.phoneNumber.match(/^\+(\d{1,4})/);
              if (match) {
                const detectedCode = '+' + match[1];
                // Check if this country code exists in our list
                const codeExists = countryCodes.some(c => c.code === detectedCode);
                if (codeExists) {
                  setSelectedCountryCodes(prevCodes => ({
                    ...prevCodes,
                    [serviceId]: detectedCode
                  }));
                }
              }
            }
            
            return updatedService;
          }
          return service;
        })
      };
      
      // Update preview URLs
      setTimeout(() => updatePreviewUrls(updated), 100);
      
      return updated;
    });
    setError('');
    setSuccess('');
  };

  const handleCountryCodeChange = (serviceId, countryCode) => {
    setSelectedCountryCodes(prev => ({
      ...prev,
      [serviceId]: countryCode
    }));
    
    // Update phone number with new country code
    setConfig(prev => {
      const updated = {
        ...prev,
        services: prev.services.map(service => {
          if (service.id === serviceId) {
            const currentNumber = service.phoneNumber || '';
            // Remove old country code and add new one
            const numberWithoutCode = currentNumber.replace(/^\+\d{1,4}/, '').replace(/^\+/, '');
            const newNumber = countryCode + numberWithoutCode;
            return { ...service, phoneNumber: formatPhoneNumberForDisplay(newNumber) };
          }
          return service;
        })
      };
      setTimeout(() => updatePreviewUrls(updated), 100);
      return updated;
    });
  };

  const handleDefaultMessageChange = (value) => {
    setConfig(prev => {
      const updated = {
        ...prev,
        defaultMessage: value,
        services: prev.services.map(service => ({
          ...service,
          message: service.message || value // Update empty messages
        }))
      };
      updatePreviewUrls(updated);
      return updated;
    });
    setError('');
    setSuccess('');
  };

  const handleServiceNameChange = (value) => {
    setConfig(prev => {
      const updated = { ...prev, serviceName: value };
      // Update default message to include new service name
      const updatedMessage = `Hello, I need assistance with ${value}`;
      updated.defaultMessage = updatedMessage;
      updated.services = updated.services.map(service => ({
        ...service,
        message: service.message || updatedMessage
      }));
      updatePreviewUrls(updated);
      return updated;
    });
    setError('');
    setSuccess('');
  };

  const handleUseDefaultMessage = (serviceId) => {
    setConfig(prev => {
      const updated = {
        ...prev,
        services: prev.services.map(service => {
          if (service.id === serviceId) {
            return { ...service, message: prev.defaultMessage };
          }
          return service;
        })
      };
      updatePreviewUrls(updated);
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Validate all phone numbers
      for (const service of config.services) {
        if (service.phoneNumber) {
          const validation = validatePhoneNumber(service.phoneNumber);
          if (!validation.valid) {
            throw new Error(`${service.label}: ${validation.error}`);
          }
        }
      }

      await updateWhatsAppConfig(config);
      setSuccess('WhatsApp configuration saved successfully!');
      setOriginalConfig(JSON.parse(JSON.stringify(config)));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving config:', err);
      setError(err.message || 'Failed to save WhatsApp configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalConfig) {
      setConfig(JSON.parse(JSON.stringify(originalConfig)));
      updatePreviewUrls(originalConfig);
    }
    setError('');
    setSuccess('');
  };

  const handleTestLink = (serviceId) => {
    const url = previewUrls[serviceId];
    if (url && url !== 'Invalid phone number' && url !== 'Enter phone number to see preview') {
      window.open(url, '_blank');
    }
  };

  const hasChanges = () => {
    if (!originalConfig) return false;
    return JSON.stringify(config) !== JSON.stringify(originalConfig);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="whatsapp-management" style={{ padding: '20px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">
          <i className="bi bi-whatsapp me-2 text-success"></i>
          WhatsApp Customer Service Management
        </h2>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          <i className="bi bi-check-circle me-2"></i>
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
        </div>
      )}

      {/* Service Name Configuration */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">
            <i className="bi bi-gear me-2"></i>
            General Settings
          </h5>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <label htmlFor="serviceName" className="form-label">
              Service Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              id="serviceName"
              value={config.serviceName}
              onChange={(e) => handleServiceNameChange(e.target.value)}
              placeholder="Enter service name (e.g., Circana)"
            />
            <small className="text-muted">This name will be used in default messages</small>
          </div>

          <div className="mb-3">
            <label htmlFor="defaultMessage" className="form-label">
              Default Message <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              id="defaultMessage"
              rows="3"
              value={config.defaultMessage}
              onChange={(e) => handleDefaultMessageChange(e.target.value)}
              placeholder="Hello, I need assistance with your service"
            />
            <small className="text-muted">This message will be used if individual service messages are not set</small>
          </div>
        </div>
      </div>

      {/* Customer Service Options */}
      <div className="card mb-4">
        <div className="card-header bg-success text-white">
          <h5 className="mb-0">
            <i className="bi bi-headset me-2"></i>
            Customer Service Numbers
          </h5>
        </div>
        <div className="card-body">
          {config.services.map((service, index) => (
            <div key={service.id} className={`mb-4 ${index < config.services.length - 1 ? 'border-bottom pb-4' : ''}`}>
              <h6 className="mb-3">
                <i className="bi bi-person-circle me-2"></i>
                {service.label}
              </h6>
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor={`phone-${service.id}`} className="form-label">
                    WhatsApp Number <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <select
                      className="form-select"
                      style={{ maxWidth: '180px' }}
                      value={selectedCountryCodes[service.id] || '+1'}
                      onChange={(e) => handleCountryCodeChange(service.id, e.target.value)}
                    >
                      {countryCodes.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.code}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="form-control"
                      id={`phone-${service.id}`}
                      value={service.phoneNumber}
                      onChange={(e) => handleServiceChange(service.id, 'phoneNumber', e.target.value)}
                      placeholder={service.id === '1' ? '+14083335900' : '+923174533412'}
                    />
                  </div>
                  <small className="text-muted">
                    Enter number with country code (e.g., +14083335900). The + sign will be kept.
                  </small>
                  {service.phoneNumber && (
                    <div className="mt-2">
                      <small className="text-info">
                        <i className="bi bi-info-circle me-1"></i>
                        For URL: {formatPhoneNumberForUrl(service.phoneNumber) || 'Invalid'} (without + sign)
                      </small>
                    </div>
                  )}
                </div>

                <div className="col-md-6 mb-3">
                  <label htmlFor={`message-${service.id}`} className="form-label">
                    Custom Message (Optional)
                  </label>
                  <div className="input-group">
                    <textarea
                      className="form-control"
                      id={`message-${service.id}`}
                      rows="3"
                      value={service.message}
                      onChange={(e) => handleServiceChange(service.id, 'message', e.target.value)}
                      placeholder={config.defaultMessage}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => handleUseDefaultMessage(service.id)}
                      title="Use default message"
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                    </button>
                  </div>
                  <small className="text-muted">
                    Leave empty to use default message
                  </small>
                </div>
              </div>

              {/* Preview Section */}
              <div className="mt-3 p-3 bg-light rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="flex-grow-1">
                    <small className="text-muted d-block mb-1">WhatsApp Link Preview:</small>
                    <code className="text-break" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                      {previewUrls[service.id] || 'Enter phone number to see preview'}
                    </code>
                    {previewUrls[service.id] && 
                     previewUrls[service.id] !== 'Invalid phone number' && 
                     previewUrls[service.id] !== 'Enter phone number to see preview' && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          Note: + sign is removed in URL (wa.me format requirement)
                        </small>
                      </div>
                    )}
                  </div>
                  {previewUrls[service.id] && 
                   previewUrls[service.id] !== 'Invalid phone number' && 
                   previewUrls[service.id] !== 'Enter phone number to see preview' && (
                    <button
                      className="btn btn-sm btn-success ms-3"
                      onClick={() => handleTestLink(service.id)}
                      title="Test WhatsApp link"
                    >
                      <i className="bi bi-whatsapp me-1"></i>
                      Test
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2">
        <button
          className="btn btn-secondary"
          onClick={handleCancel}
          disabled={saving || !hasChanges()}
        >
          <i className="bi bi-x-circle me-2"></i>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !hasChanges()}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Saving...
            </>
          ) : (
            <>
              <i className="bi bi-save me-2"></i>
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default WhatsAppManagement;

